import { useMemo } from 'react'

import { useUnit } from 'effector-react'
import type { DateRange } from 'react-day-picker'
import { useQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import {
  getFailedAutoFollows,
  getFollowedAutoFollows,
  getQueuedAutoFollows,
} from '@/modules/automations/service/automations-service.ts'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import type { AutoFollowResponses } from '../types'

export type AutoFollowTableKeys = 'followed' | 'queued' | 'failed'

export interface UseAutoFollowTableProps {
  key: AutoFollowTableKeys
  dateRange: DateRange
  creatorId?: number
  page: number
  size: number
}

export interface TableSelectionType {
  key: AutoFollowTableKeys
  displayName: string
  description: string
}

const dataFetchFunctions = {
  followed: getFollowedAutoFollows,
  queued: getQueuedAutoFollows,
  failed: getFailedAutoFollows,
}

export const tableSelectionVariants: TableSelectionType[] = [
  {
    key: 'followed',
    displayName: 'Auto Follow History',
    description: 'The fans who have been followed recently',
  },
  {
    key: 'queued',
    displayName: 'Queued Follows',
    description: 'The fans who are waiting to be followed in the queue',
  },
  {
    key: 'failed',
    displayName: 'Follow Errors',
    description: "The fans who haven't been followed",
  },
]

export const useAutoFollowTable = ({
  key,
  dateRange,
  page,
  size,
}: Omit<UseAutoFollowTableProps, 'creatorId'>) => {
  const creatorId = useUnit($creatorId)
  const { data, isFetching, isIdle, refetch } = useQuery(
    ['auto-follow', key, dateRange, creatorId, { page, size }],
    () =>
      _fetchAutoFollows({ key, dateRange, creatorId: creatorId!, page, size }),
    {
      keepPreviousData: true,
      enabled: !!creatorId,
      select: (response) => {
        response.fans.filter(isTruthyAndNotEmpty)

        return response
      },
    },
  )

  return useMemo(() => {
    const totalPages = Math.ceil(data?.total ?? 0 / size)

    return {
      data,
      isFetching: isFetching || isIdle,
      totalPages,
      refetch,
    }
  }, [data, isFetching, isIdle, size, refetch])
}

const _fetchAutoFollows = async ({
  key,
  ...props
}: UseAutoFollowTableProps): Promise<AutoFollowResponses> => {
  const fetchData = dataFetchFunctions[key]

  if (fetchData) {
    return await fetchData({ ...props })
  }

  throw new Error('Error while fetching auto follow')
}
